import {
  MenuDivider,
  MenuItemOption,
  MenuList,
  MenuOptionGroup,
} from '@chakra-ui/react';
import { find, isEmpty } from 'lodash';
import { useEffect, useState } from 'react';
import invariant from 'tiny-invariant';
import { useTranslation } from 'react-i18next';
import {
  getAllSparkelCategories,
  SPARKEL_TO_CATEGORIES_MAP,
  SparkelCategory,
} from '../../../domain/category-mapping';
import { useViewer } from '../../common/ForgeViewer';

type MagicPolygonCategoryMenuProps = {
  selectedCategories: string[];
  setSelectedCategories: (categories: string[]) => void;
};

export default function MagicPolygonCategoryMenu({
  selectedCategories,
  setSelectedCategories,
}: MagicPolygonCategoryMenuProps): React.ReactElement {
  const { t } = useTranslation(['project', 'categories']);
  const {
    isAllModelsLoaded,
    loadedModels,
    visibilityManager: { hiddenModelUrns },
  } = useViewer();

  const [eligibleCategories, setEligibleCategories] = useState<Set<string>>(
    new Set()
  );

  // Query models for available IFC classes and Revit categories
  useEffect(() => {
    const findAndSetEligibleCategories = async () => {
      if (isAllModelsLoaded && !isEmpty(loadedModels)) {
        const newEligibleCategories = new Set<string>();

        for (const modelUrn of Object.keys(loadedModels)) {
          if (modelUrn in hiddenModelUrns) {
            continue;
          }
          const model = loadedModels[modelUrn];
          invariant(model, 'model not found');
          const ifcClassDbIds = await model.findProperty('IfcClass');
          const revitCategoryDbIds = await model.findProperty('Category');

          const ifcCategoriesInModel: string[] = await new Promise(
            (resolve) => {
              model.getBulkProperties2(
                ifcClassDbIds,
                {
                  propFilter: ['IfcClass'],
                },
                (propertyResults) => {
                  resolve(
                    propertyResults.map(
                      (result) =>
                        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                        find(result.properties, ['attributeName', 'IfcClass'])!
                          .displayValue as string
                    )
                  );
                }
              );
            }
          );
          const revitCategoriesInModel: string[] = await new Promise(
            (resolve) => {
              model.getBulkProperties2(
                revitCategoryDbIds,
                {
                  propFilter: ['Category'],
                },
                (propertyResults) => {
                  resolve(
                    propertyResults.map(
                      (result) =>
                        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                        find(result.properties, ['attributeName', 'Category'])!
                          .displayValue as string
                    )
                  );
                }
              );
            }
          );
          for (const ifcCategory of ifcCategoriesInModel) {
            newEligibleCategories.add(ifcCategory);
          }
          for (const revitCategory of revitCategoriesInModel) {
            newEligibleCategories.add(revitCategory);
          }
        }
        setEligibleCategories(newEligibleCategories);
      }
    };

    findAndSetEligibleCategories();
  }, [hiddenModelUrns, isAllModelsLoaded, loadedModels]);

  // Filter Sparkel categories to only show those that have corresponding IFC/Revit categories in models
  const availableCategories = getAllSparkelCategories().filter((category) => {
    // Always show "Other" category
    if (category === SparkelCategory.Other) {
      return true;
    }
    const categoryMapping =
      SPARKEL_TO_CATEGORIES_MAP[category as SparkelCategory];
    if (!categoryMapping) {
      return false;
    }
    // Check if any of the IFC classes or Revit categories for this Sparkel category exist in the models
    return (
      categoryMapping.ifcClasses.some((ifcClass) =>
        eligibleCategories.has(ifcClass)
      ) ||
      categoryMapping.revitCategories.some((revitCategory) =>
        eligibleCategories.has(revitCategory)
      )
    );
  });

  const allSelected = availableCategories.length === selectedCategories.length;

  const handleChange = (selected: string | string[]) => {
    if (Array.isArray(selected)) {
      setSelectedCategories(selected);
    } else {
      console.error('Selected categories should always be array');
    }
  };

  const handleAllEvent = (allEvent: string | string[]) => {
    // AllEvent.length > 0 means "All" is checked
    if (allEvent.length > 0) {
      setSelectedCategories(availableCategories.map((cat) => cat));
    } else {
      setSelectedCategories([]);
    }
  };

  return (
    <MenuList maxHeight={'80vh'} overflow={'auto'} zIndex={99} fontSize="xs">
      <MenuOptionGroup
        onChange={(e: string | string[]) => handleAllEvent(e)}
        type="checkbox"
        value={allSelected ? ['all'] : []}
      >
        <MenuItemOption value="all" key={'all'}>
          {t('categories:all')}
        </MenuItemOption>
      </MenuOptionGroup>
      <MenuDivider />
      <MenuOptionGroup
        onChange={(e: string | string[]) => {
          handleChange(e);
        }}
        type="checkbox"
        value={selectedCategories}
      >
        {availableCategories.map((category) => {
          return (
            <MenuItemOption value={category} key={category}>
              {category}
            </MenuItemOption>
          );
        })}
      </MenuOptionGroup>
    </MenuList>
  );
}
